import { MaterialIcon, TableWrapper } from '@components';

import {
  Stack,
  Typography,
  Table as MuiTable,
  TableRow,
  TableBody,
  TableHead,
  TableCell,
  Avatar,
  Tooltip,
  Chip,
  Skeleton,
} from '@mui/material';
import { StandingAccessReportUsageFragmentFragment } from '@api/gql/graphql';
import { LoadingButton } from '@mui/lab';
import AssessmentReportLastUsed from './AssessmentReportLastUsed';
import AssessmentReportIdentity from './AssessmentReportIdentity';

function AssessmentReportDrawerTableItem({
  usage,
  selectedConnections,
  onSelectConnection,
  withAccessFlow,
}: {
  usage: StandingAccessReportUsageFragmentFragment;
  selectedConnections: string[];
  onSelectConnection: (id: string) => void;
  withAccessFlow?: boolean;
}) {
  return (
    <TableRow>
      <TableCell>
        {usage.identity && <AssessmentReportIdentity name={usage.identity.name} email={usage.identity.email} />}
      </TableCell>
      <TableCell>
        <Stack direction="column" spacing={0.5} alignItems="flex-start">
          {usage.identityLinks?.map((connection, index) => (
            <ItemConnection
              key={`${index}-${usage.id}`}
              connection={connection}
              onSelect={onSelectConnection}
              selected={selectedConnections}
            />
          ))}
        </Stack>
      </TableCell>
      <TableCell>
        <AssessmentReportLastUsed lastUsedAt={usage.lastUsedAt} />
      </TableCell>
      {withAccessFlow && (
        <TableCell>
          <CheckIcon checked={usage.inAccessFlow ?? false} />
        </TableCell>
      )}
      <TableCell>
        <CheckIcon checked={usage.revoked ?? false} />
      </TableCell>
    </TableRow>
  );
}

interface CheckIconProps {
  checked?: boolean;
}

function CheckIcon({ checked = false }: CheckIconProps) {
  return (
    <Avatar
      sx={(theme) => ({
        width: 16,
        height: 16,
        bgcolor: checked ? 'success.main' : 'transparent',
        border: checked ? 'none' : `1px solid ${theme.palette.divider}`,
      })}
    >
      {checked && <MaterialIcon symbol="check" size="small" sx={{ color: 'white' }} />}
    </Avatar>
  );
}

interface AssessmentReportDrawerTableProps {
  totalCount: number;
  usages: StandingAccessReportUsageFragmentFragment[];
  onLoadMore: () => void;
  isLoadingMore: boolean;
  isFetching?: boolean;
  onSelectConnection: (id: string) => void;
  selectedConnections: string[];
  withAccessFlow?: boolean;
}

const STEP_1_DESCRIPTION = 'Create Access Flow';
const STEP_2_DESCRIPTION = 'Revoke Access';

export default function AssessmentReportDrawerTable({
  totalCount,
  usages,
  onLoadMore,
  isLoadingMore,
  isFetching,
  onSelectConnection,
  selectedConnections,
  withAccessFlow,
}: AssessmentReportDrawerTableProps) {
  return (
    <Stack direction="column" spacing={2}>
      <TableWrapper noBackground>
        <MuiTable>
          <TableHead>
            <TableRow>
              <TableCell component="th" size="medium">
                Identity
              </TableCell>
              <TableCell component="th" size="medium">
                Assignment type
              </TableCell>
              <TableCell component="th" size="medium">
                <Tooltip
                  title="Apono scans 90 days of activity to determine if an identity has been used recently."
                  arrow
                  placement="top"
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="subtitle2">Last used</Typography>
                    <MaterialIcon symbol="info" size="small" weight={500} />
                  </Stack>
                </Tooltip>
              </TableCell>
              {withAccessFlow ? (
                <>
                  <TableCell component="th" size="medium">
                    <Tooltip title={STEP_1_DESCRIPTION} arrow placement="top">
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="subtitle2">Step 1</Typography>
                        <MaterialIcon symbol="info" size="small" weight={500} />
                      </Stack>
                    </Tooltip>
                  </TableCell>
                  <TableCell component="th" size="medium">
                    <Tooltip title={STEP_2_DESCRIPTION} arrow placement="top">
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="subtitle2">Step 2</Typography>
                        <MaterialIcon symbol="info" size="small" weight={500} />
                      </Stack>
                    </Tooltip>
                  </TableCell>
                </>
              ) : (
                <TableCell component="th" size="medium">
                  <Typography variant="subtitle2">{STEP_2_DESCRIPTION}</Typography>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching ? (
              <>
                <AssessmentReportDrawerTableLoaderRow />
                <AssessmentReportDrawerTableLoaderRow />
                <AssessmentReportDrawerTableLoaderRow />
              </>
            ) : (
              usages.map((usage) => (
                <AssessmentReportDrawerTableItem
                  key={usage.id}
                  usage={usage}
                  selectedConnections={selectedConnections}
                  onSelectConnection={onSelectConnection}
                  withAccessFlow={withAccessFlow}
                />
              ))
            )}
          </TableBody>
        </MuiTable>
      </TableWrapper>
      {totalCount > usages.length && (
        <Stack direction="row" justifyContent="center">
          <LoadingButton variant="outlined" onClick={() => onLoadMore()} loading={isLoadingMore} size="small">
            Load more
          </LoadingButton>
        </Stack>
      )}
    </Stack>
  );
}

interface ConnectionProps {
  type: string;
  id?: string | null;
  name?: string | null;
  revoked?: boolean | null;
}

function ItemConnection({
  connection,
  onSelect,
  selected,
}: {
  connection: ConnectionProps;
  selected: string[];
  onSelect: (id: string) => void;
}) {
  const isSelected = selected.includes(connection.id ?? '');

  if (connection.type === 'USER')
    return (
      <Chip
        label={<ItemConnectionName name="Direct" revoked={connection.revoked ?? false} />}
        size="small"
        variant={isSelected ? 'filled' : 'outlined'}
        clickable={!connection.revoked && !isSelected}
        onClick={!isSelected ? () => onSelect(connection.id ?? '') : undefined}
      />
    );
  if (connection.type === 'GROUP' && connection.name)
    return (
      <Chip
        avatar={<MaterialIcon symbol="group" size="small" />}
        label={<ItemConnectionName name={connection.name} revoked={connection.revoked ?? false} />}
        variant={isSelected ? 'filled' : 'outlined'}
        size="small"
        clickable={!connection.revoked && !isSelected}
        onClick={!isSelected ? () => onSelect(connection.id ?? '') : undefined}
      />
    );
  return <></>;
}

function ItemConnectionName({ name, revoked }: { name: string; revoked: boolean }) {
  return (
    <>
      <span style={{ textDecoration: revoked ? 'line-through' : 'none' }}>{name}</span>
      {revoked && (
        <Typography variant="caption" color="error.main">
          {' '}
          revoked
        </Typography>
      )}
    </>
  );
}

function AssessmentReportDrawerTableLoaderRow({ withAccessFlow }: { withAccessFlow?: boolean }) {
  return (
    <TableRow>
      <TableCell>
        <Skeleton variant="text" width="100%" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width="100%" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width="100%" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width="100%" />
      </TableCell>
      {withAccessFlow && (
        <TableCell>
          <Skeleton variant="text" width="100%" />
        </TableCell>
      )}
    </TableRow>
  );
}
